import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { 
  CreditCard, 
  Check, 
  Star, 
  Calendar, 
  Download,
  Zap,
  Shield,
  Users,
  ArrowRight
} from 'lucide-react'
import api from '../utils/api'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import { PaymentMethodManager } from '../components/StripeElements'

const PaymentPage = () => {
  const { t } = useTranslation()
  const [selectedPlan, setSelectedPlan] = useState(null)
  const queryClient = useQueryClient()

  // Fetch current subscription
  const { data: subscription = {}, isLoading: subscriptionLoading } = useQuery({
    queryKey: ['subscription-status'],
    queryFn: async () => {
      const response = await api.get('/subscription/status')
      return response.data
    },
  })

  // Fetch available plans
  const { data: availablePlans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const response = await api.get('/subscription/plans')
      return response.data.plans || []
    },
  })

  // Fetch billing history
  const { data: paymentHistory = [], isLoading: historyLoading } = useQuery({
    queryKey: ['payment-history'],
    queryFn: async () => {
      const response = await api.get('/subscription/payment-history')
      return response.data.payment_history || []
    },
  })

  // Upgrade subscription mutation
  const upgradeMutation = useMutation({
    mutationFn: async (planName) => {
      const response = await api.post('/subscription/upgrade', { plan_name: planName })
      return response.data
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
        // Handle successful upgrade
        alert(data.message || 'Subscription upgraded successfully!')
      }
    },
    onError: (error) => {
      console.error('Upgrade failed:', error)
      alert(error.response?.data?.detail || 'Upgrade failed. Please try again.')
    }
  })

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: async (atPeriodEnd = true) => {
      const response = await api.post('/subscription/cancel', { at_period_end: atPeriodEnd })
      return response.data
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
        alert(data.message || 'Subscription cancelled successfully!')
      }
    },
    onError: (error) => {
      console.error('Cancellation failed:', error)
      alert(error.response?.data?.detail || 'Cancellation failed. Please try again.')
    }
  })

  // Use API plans data or fallback to default
  const plans = availablePlans.length > 0 ? availablePlans.map(plan => ({
    ...plan,
    id: plan.name,
    current: subscription.plan_name === plan.name,
    price: plan.price_cents / 100,
    popular: plan.name === 'pro'
  })) : []

  const handleUpgrade = (planName) => {
    if (planName !== 'free' && planName !== subscription.plan_name) {
      setSelectedPlan(planName)
      upgradeMutation.mutate(planName)
    }
  }

  const handleCancel = () => {
    if (confirm(t('payment.confirmCancel'))) {
      cancelMutation.mutate(true) // Cancel at period end
    }
  }

  return (
    <div className="page-transition space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('payment.subscriptionBilling')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('payment.choosePlan')}
        </p>
      </div>

      {/* Current Subscription */}
      {!subscriptionLoading && (
        <Card>
          <Card.Header>
            <Card.Title>{t('plan.current')}</Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                  {subscription.plan_display_name || t('plan.free.name')} {t('plan.current').split(' ')[0]}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {subscription.plan_name === 'free' 
                    ? t('plan.noBilling')
                    : subscription.next_billing_date 
                      ? `${t('plan.nextBilling', { date: new Date(subscription.next_billing_date).toLocaleDateString() })}` 
                      : t('plan.status.' + (subscription.plan_status || 'active'))
                  }
                </p>
                {subscription.subscription_end_date && (
                  <p className="text-sm text-orange-600 dark:text-orange-400">
                    {t('plan.endsOn', { date: new Date(subscription.subscription_end_date).toLocaleDateString() })}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {(subscription.characters_used || 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  / {subscription.monthly_character_limit === -1 
                      ? t('account.unlimited') 
                      : (subscription.monthly_character_limit || 10000).toLocaleString()
                    } {t('translation.characters').toLowerCase()} {t('stats.charactersTranslated').toLowerCase().split(' ')[1]}
                </p>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {subscription.usage_percentage?.toFixed(1)}% used
                </div>
              </div>
            </div>
            
            {subscription.monthly_character_limit && subscription.monthly_character_limit !== -1 && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${Math.min(100, subscription.usage_percentage || 0)}%` 
                    }}
                  />
                </div>
              </div>
            )}

            {/* Cancel Subscription Button */}
            {subscription.plan_name !== 'free' && subscription.plan_status === 'active' && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={cancelMutation.isPending}
                  className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                >
                  {cancelMutation.isPending ? t('button.cancelling') : t('button.cancelSubscription')}
                </Button>
              </div>
            )}
          </Card.Content>
        </Card>
      )}

      {/* Pricing Plans */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
          {t('plan.choosePlan')}
        </h2>
        {plansLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <Card.Content className="p-6 animate-pulse">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-6" />
                  <div className="space-y-2 mb-8">
                    {[...Array(4)].map((_, j) => (
                      <div key={j} className="h-3 bg-gray-200 dark:bg-gray-700 rounded" />
                    ))}
                  </div>
                  <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
                </Card.Content>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative ${
                plan.popular 
                  ? 'ring-2 ring-primary-500 shadow-lg scale-105' 
                  : plan.current 
                    ? 'ring-2 ring-green-500' 
                    : ''
              }`}
            >
              {plan.popular && !plan.current && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    <Star className="w-4 h-4 mr-1" />
                    {t('plan.mostPopular')}
                  </span>
                </div>
              )}
              
              {plan.current && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    <Check className="w-4 h-4 mr-1" />
                    {t('plan.currentPlan')}
                  </span>
                </div>
              )}
              
              {plan.popular && plan.current && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    <Check className="w-4 h-4 mr-1" />
                    {t('plan.currentPlan')}
                  </span>
                </div>
              )}

              <Card.Content className="text-center p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    ${plan.price}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    /{t('billing.perMonth')}
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {plan.description}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {(plan.features || []).map((feature, index) => (
                    <li key={index} className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleUpgrade(plan.name)}
                  variant={plan.current ? 'outline' : plan.popular ? 'primary' : 'outline'}
                  className="w-full"
                  disabled={plan.current || upgradeMutation.isPending}
                  loading={upgradeMutation.isPending && selectedPlan === plan.name}
                >
                  {plan.current 
                    ? t('button.currentPlan')
                    : plan.name === 'free' 
                      ? t('button.downgradeToFree')
                      : t('button.upgradeTo', { plan: plan.display_name })
                  }
                  {!plan.current && plan.name !== 'free' && (
                    <ArrowRight className="w-4 h-4 ml-2" />
                  )}
                </Button>
              </Card.Content>
            </Card>
            ))}
          </div>
        )}
      </div>

      {/* Features Comparison */}
      <Card>
        <Card.Header>
          <Card.Title>{t('features.comparison')}</Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 font-medium text-gray-900 dark:text-white">{t('features.feature')}</th>
                  {plans.map((plan) => (
                    <th key={plan.name} className="text-center py-3 font-medium text-gray-900 dark:text-white">
                      {plan.display_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="space-y-2">
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 text-gray-700 dark:text-gray-300">{t('features.monthlyLimit')}</td>
                  {plans.map((plan) => (
                    <td key={plan.name} className="text-center py-3">
                      {plan.monthly_character_limit === -1 
                        ? t('account.unlimited') 
                        : `${(plan.monthly_character_limit / 1000).toFixed(0)}K`
                      }
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 text-gray-700 dark:text-gray-300">{t('features.phraseIntegration')}</td>
                  {plans.map((plan) => (
                    <td key={plan.name} className="text-center py-3">
                      {plan.has_phrase_integration ? '✓' : '✗'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 text-gray-700 dark:text-gray-300">{t('features.prioritySupport')}</td>
                  {plans.map((plan) => (
                    <td key={plan.name} className="text-center py-3">
                      {plan.has_priority_support ? '✓' : '✗'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 text-gray-700 dark:text-gray-300">{t('features.qualityReview')}</td>
                  {plans.map((plan) => (
                    <td key={plan.name} className="text-center py-3">
                      {plan.has_quality_review ? '✓' : '✗'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Card.Content>
      </Card>

      {/* Payment Methods Management */}
      <PaymentMethodManager />

      {/* Billing History */}
      <Card>
        <Card.Header>
          <Card.Title>{t('billing.history')}</Card.Title>
        </Card.Header>
        <Card.Content>
          {historyLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : paymentHistory.length > 0 ? (
            <div className="space-y-4">
              {paymentHistory.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {payment.description || t('billing.subscriptionCreation')}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </p>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        payment.status === 'succeeded' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : payment.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {t(`billing.${payment.status}`)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${(payment.amount / 100).toFixed(2)} {payment.currency?.toUpperCase()}
                    </span>
                    {payment.receipt_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(payment.receipt_url, '_blank')}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {t('billing.noHistory')}
              </p>
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  )
}

export default PaymentPage